import React, { useState } from "react";
import { Row, Col } from "react-bootstrap";
import {
  func,
  bool,
  object,
  number,
  string,
  oneOfType,
  instanceOf,
} from "prop-types";
import { connect } from "react-redux";
import {
  smsOptinGrandfatheredCompanyIds,
  screenReaderCompatibleTests,
} from "utils/helperConstants";
import AssessmentMode from "./AssessmentMode";
import Notifications from "./Notifications";
import TestingEventExpiration from "./TestingEventExpiration";
import AdjustedTime from "./AdjustedTime";
import ReuseResults from "./ReuseResults";
import ScreenReaderCompatibility from "./ScreenReaderCompatibility";
import Proctoring from "./Proctoring";
import { checkBatteryHasTimedTest, getCaretImage } from "./helpers";

const InvitationSettings = ({
  emailTemplateId,
  emailTemplates,
  eventExpirationDays,
  eventReminderDays,
  featureFlags,
  fromScheduleExistingCandidates,
  fromScheduleNewCandidates,
  handleAdjustedTimeMultiplier,
  handleChangeEmailTemplateId,
  handleChangeNotifyEmail,
  handleChangeSendReminder,
  handleChangeReuseResults,
  handleEventExpirationDaysDropdown,
  handleEventReminderDaysDropdown,
  handleSelectHoursDelay,
  handleSupervisedOnChange,
  handleTestingEventExpirationToggle,
  hoursDelay,
  isAU,
  notifyEmail,
  reuseCandidateResults, // boolean for this specific candidate
  reuseResults, // object with months and reuseEnabled
  sendReminder,
  showTimeFactor,
  supervised,
  testBatterySubTests,
  testingEventExpiration,
  timeFactor,
  vhs,
  job,
  testBatteryId,
  jobDetailId,
  testMakerTests,
  testBatteryChosen,
  userData,
  selectedEventExpiration,
  handleSelectEventExpiration,
  eventExpirationDate,
  handleEventExpirationDateChange,
  eventExpirationTime,
  handleEventExpirationTime,
  showEventExpirationTimeError,
  eventExpirationTimeZone,
  handleTimezoneDropdown,
  handleEventReminderEmailTemplateDropdown,
  eventReminderEmailTemplateId,
  handleChangeNotifySMS,
  notifySMS,
  companyInfo,
  screenReaderCompatibility,
  handleChangeScreenReaderCompatibility,
  proctoring,
  handleChangeProctoring,
  showProctoring,
  proctoringConfiguration,
  isProctoringOptedIn,
}) => {
  const [viewAdvancedSettings, setViewAdvancedSettings] = useState(false);

  const onViewAdvancedSettings = () => {
    setViewAdvancedSettings(!viewAdvancedSettings);
  };

  const isInsightUser = userData && userData.partnerAccountId === 47;
  const locale = userData && userData.locale;
  const companyName = userData && userData.companyName;
  const companyAccountId = userData && userData.companyAccountId;
  const isInternal = Boolean(userData && userData.internal);

  // Enable sms invitation and reminder for non insight, Talent Success Suite, and grandfathered sms company accounts
  const isSMSOn =
    !isInsightUser &&
    (vhs === 5 || smsOptinGrandfatheredCompanyIds.includes(companyAccountId)) &&
    companyInfo &&
    companyInfo.get("smsInvitationAndReminder");

  const testBatteryToBeChecked =
    fromScheduleExistingCandidates && testBatteryChosen
      ? testBatteryChosen.get("subTests")
      : job &&
        job.getIn([jobDetailId, "batteries", "data", String(testBatteryId)]);

  const showAdjustedTime = Boolean(
    showTimeFactor &&
      testBatterySubTests &&
      checkBatteryHasTimedTest(
        testBatterySubTests,
        testBatteryToBeChecked,
        testMakerTests,
        !!fromScheduleExistingCandidates
      )
  );

  const showScreenReaderCompatibility = !!(
    testBatterySubTests &&
    testBatterySubTests.some((test) =>
      screenReaderCompatibleTests.includes(Number(test.get("testId")))
    )
  );

  const showAdvancedSettingsDropdown =
    (fromScheduleExistingCandidates && (showAdjustedTime || showProctoring)) ||
    fromScheduleNewCandidates;

  const companyDefaultTimezone =
    companyInfo && companyInfo.get("defaultCandidateInvitationTimeZone");

  return (
    <>
      <Row>
        <Col>
          <h4 className="mb-3">Invitation Settings</h4>
          {!isInsightUser ? (
            <AssessmentMode
              supervised={supervised}
              handleSupervisedOnChange={handleSupervisedOnChange}
            />
          ) : null}
          {!supervised && (
            <Notifications
              emailTemplates={emailTemplates}
              emailTemplateId={emailTemplateId}
              handleChangeEmailTemplateId={handleChangeEmailTemplateId}
              handleChangeNotifyEmail={handleChangeNotifyEmail}
              notifyEmail={notifyEmail}
              vhs={vhs}
              hoursDelay={hoursDelay}
              handleSelectHoursDelay={handleSelectHoursDelay}
              companyName={companyName}
              isInsightUser={isInsightUser}
              locale={locale}
              handleChangeNotifySMS={handleChangeNotifySMS}
              notifySMS={notifySMS}
              job={job}
              isSMSOn={isSMSOn}
              fromScheduleExistingCandidates={fromScheduleExistingCandidates}
              companyDefaultTimezone={companyDefaultTimezone}
              isAU={isAU}
              isInternal={isInternal}
              proctoring={proctoring}
              showProctoring={showProctoring}
            />
          )}
          {showAdvancedSettingsDropdown && (
            <div className="mt-3 mb-4 d-inline-block">
              <button
                type="button"
                className="btn-as-link"
                style={{ color: "#425cc7" }}
                onClick={onViewAdvancedSettings}
              >
                Advanced Settings
                <span>{getCaretImage(viewAdvancedSettings)}</span>
              </button>
            </div>
          )}
        </Col>
      </Row>
      {viewAdvancedSettings && (
        <Row>
          <Col>
            {!fromScheduleExistingCandidates && (
              <TestingEventExpiration
                testingEventExpiration={testingEventExpiration}
                eventExpirationDays={eventExpirationDays}
                sendReminder={sendReminder}
                eventReminderDays={eventReminderDays}
                handleTestingEventExpirationToggle={
                  handleTestingEventExpirationToggle
                }
                handleEventExpirationDaysDropdown={
                  handleEventExpirationDaysDropdown
                }
                handleEventReminderDaysDropdown={
                  handleEventReminderDaysDropdown
                }
                handleChangeSendReminder={handleChangeSendReminder}
                isAU={isAU}
                selectedEventExpiration={selectedEventExpiration}
                handleSelectEventExpiration={handleSelectEventExpiration}
                eventExpirationDate={eventExpirationDate}
                handleEventExpirationDateChange={
                  handleEventExpirationDateChange
                }
                eventExpirationTime={eventExpirationTime}
                handleEventExpirationTime={handleEventExpirationTime}
                showEventExpirationTimeError={showEventExpirationTimeError}
                eventExpirationTimeZone={eventExpirationTimeZone}
                handleTimezoneDropdown={handleTimezoneDropdown}
                handleEventReminderEmailTemplateDropdown={
                  handleEventReminderEmailTemplateDropdown
                }
                eventReminderEmailTemplateId={eventReminderEmailTemplateId}
                emailTemplates={emailTemplates}
                isInsightUser={isInsightUser}
                companyName={companyName}
                vhs={vhs}
                notifySMS={notifySMS}
              />
            )}
            {showProctoring ? (
              <>
                <hr />
                <Proctoring
                  handleChangeProctoring={handleChangeProctoring}
                  proctoring={proctoring}
                  proctoringConfiguration={proctoringConfiguration}
                  isProctoringOptedIn={isProctoringOptedIn}
                />
              </>
            ) : null}
            {showAdjustedTime && (
              <>
                <hr />
                <AdjustedTime
                  testBatterySubTests={testBatterySubTests}
                  timeFactor={timeFactor}
                  handleAdjustedTimeMultiplier={handleAdjustedTimeMultiplier}
                  job={job}
                  testBatteryId={testBatteryId}
                  jobDetailId={jobDetailId}
                  testMakerTests={testMakerTests}
                  fromScheduleExistingCandidates={
                    fromScheduleExistingCandidates
                  }
                  locale={locale}
                  isAdjustedTimeMultiplierEnabled={
                    featureFlags &&
                    featureFlags.get("isAdjustedTimeMultiplierEnabled")
                  }
                />
              </>
            )}
            {showScreenReaderCompatibility && (
              <ScreenReaderCompatibility
                fromScheduleExistingCandidates={fromScheduleExistingCandidates}
                handleChangeScreenReaderCompatibility={
                  handleChangeScreenReaderCompatibility
                }
                job={job}
                jobDetailId={jobDetailId}
                screenReaderCompatibilityEnabled={screenReaderCompatibility}
                testBatteryId={testBatteryId}
                testBatterySubTests={testBatterySubTests}
                timeFactor={timeFactor}
              />
            )}
          </Col>
        </Row>
      )}
      {(fromScheduleExistingCandidates || viewAdvancedSettings) &&
      reuseResults.reuseEnabled ? (
        <div>
          <hr />
          <ReuseResults
            handleChangeReuseResults={handleChangeReuseResults}
            reuseCandidateResults={reuseCandidateResults}
            months={reuseResults.months}
          />
        </div>
      ) : null}
    </>
  );
};

InvitationSettings.propTypes = {
  emailTemplateId: oneOfType([number, string]),
  emailTemplates: object,
  eventExpirationDays: number,
  eventReminderDays: number,
  featureFlags: object,
  fromScheduleExistingCandidates: bool,
  fromScheduleNewCandidates: bool,
  handleAdjustedTimeMultiplier: func,
  handleChangeEmailTemplateId: func,
  handleChangeNotifyEmail: func,
  handleChangeSendReminder: func,
  handleChangeReuseResults: func,
  handleEventExpirationDaysDropdown: func,
  handleEventReminderDaysDropdown: func,
  handleSelectHoursDelay: func,
  handleSupervisedOnChange: func,
  handleTestingEventExpirationToggle: func,
  hoursDelay: number,
  isAU: bool,
  notifyEmail: bool,
  reuseResults: object,
  reuseCandidateResults: bool,
  sendReminder: bool,
  showTimeFactor: number,
  supervised: oneOfType([number, bool]),
  testBatterySubTests: oneOfType([object, bool]),
  testingEventExpiration: bool,
  timeFactor: oneOfType([number, string]),
  vhs: number,
  job: object,
  testBatteryId: number,
  jobDetailId: string,
  testMakerTests: object,
  testBatteryChosen: object,
  userData: object,
  selectedEventExpiration: string,
  handleSelectEventExpiration: func,
  eventExpirationDate: string,
  handleEventExpirationDateChange: func,
  eventExpirationTime: instanceOf(Date),
  handleEventExpirationTime: func,
  showEventExpirationTimeError: bool,
  eventExpirationTimeZone: string,
  handleTimezoneDropdown: func,
  handleEventReminderEmailTemplateDropdown: func,
  eventReminderEmailTemplateId: oneOfType([number, string]),
  handleChangeNotifySMS: func,
  notifySMS: bool,
  companyInfo: object,
  screenReaderCompatibility: bool,
  handleChangeScreenReaderCompatibility: func,
  proctoring: bool,
  handleChangeProctoring: func,
  showProctoring: bool,
  proctoringConfiguration: object,
  isProctoringOptedIn: number,
};

const mapStateToProps = (state) => ({
  userData: state.get("App").get("userData"),
});

export default connect(mapStateToProps)(InvitationSettings);
